/**
 * Multi-device session listing — pure data layer.
 *
 * For each userId, tracks every registered device (browser tab, PWA install,
 * or future linked client) that has its own Signal identity. This is the web
 * analogue of the iOS SessionManager + SignalStore.getDeviceId() pattern.
 *
 * IndexedDB schema
 * ─────────────────
 * Database name:  "kaether:multi-device:v1"  (configurable via dbName ctor arg)
 * Object store:   "devices"
 * Key path:       compound synthetic key  "<userId>\x00<deviceId>"  (string)
 *                 Using a compound string key lets us do a range scan on all
 *                 records for a userId with IDBKeyRange.bound().
 * Indexes:
 *   "by-userId"   — non-unique index on the `userId` field; used by list().
 *
 * Each row stored as a plain JSON-serialisable object matching `DeviceRecord`.
 *
 * Upsert policy (invariant documented in docs/multi-device-sessions.md):
 *   add() is LATEST-WINS — if a record with the same (userId, deviceId)
 *   already exists it is overwritten. The caller should treat this as a
 *   re-registration event (e.g. fresh browser install).  No audit log is kept
 *   of previous records; callers that need history should snapshot before
 *   calling add().
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

export type DeviceStatus = "active" | "revoked";

export interface DeviceRecord {
  /** Opaque user identifier (matches Signal address userId). */
  readonly userId: string;
  /**
   * Signal device ID for this install. Signal convention: primary device = 1,
   * linked devices > 1. The web client uses WEB_DEVICE_ID = 1 for its primary
   * device.
   */
  readonly deviceId: number;
  /**
   * Base-64-encoded public identity key (Curve25519) for this device.
   * Used for Safety Numbers and session establishment.
   */
  readonly identityPubKey: string;
  /** Signal registrationId for this device (random 14-bit integer). */
  readonly registrationId: number;
  /**
   * Human-readable label auto-derived from the user-agent or manually set
   * by the user. Examples: "Chrome on macOS", "Firefox on Windows".
   */
  readonly label: string;
  /** ISO-8601 timestamp of when this device was first registered. */
  readonly addedAt: string;
  /**
   * ISO-8601 timestamp of the most recent activity seen from this device.
   * Updated on each inbound/outbound message. Used by prune().
   */
  readonly lastSeenAt: string;
  readonly status: DeviceStatus;
}

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

/**
 * Persistent store for registered-device records. All methods are async and
 * safe to call concurrently (IndexedDB serialises writes within a transaction).
 */
export interface MultiDeviceStore {
  /**
   * Return all device records for `userId`, sorted ascending by deviceId.
   * Returns [] when no records exist. Records with status 'revoked' are
   * included — callers filter by status if they only want active devices.
   */
  list(userId: string): Promise<DeviceRecord[]>;

  /**
   * Persist `record` for (userId, deviceId). LATEST-WINS: an existing record
   * with the same (userId, deviceId) is replaced entirely. If you are
   * re-linking a previously-revoked device, status should be set to 'active'
   * in the incoming record.
   */
  add(userId: string, record: DeviceRecord): Promise<void>;

  /**
   * Set status = 'revoked' for the given (userId, deviceId) and update
   * lastSeenAt to `at` (defaults to now). No-ops silently if the record does
   * not exist. Does NOT delete the record so callers can audit the revocation.
   *
   * After marking revoked, callers are expected to drop the Signal session for
   * this deviceId via their SessionCipher / SignalProtocolStore.
   */
  markRevoked(userId: string, deviceId: number, at?: Date): Promise<void>;

  /**
   * Remove all records for `userId` whose `lastSeenAt` is older than
   * `olderThanMs` milliseconds ago. Revoked records are eligible for pruning
   * just like active ones — this is a storage-hygiene operation, not a
   * security one.
   */
  prune(userId: string, olderThanMs: number): Promise<void>;
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "devices";
const INDEX_USER = "by-userId";

/** Compound key used as the IDB primary key for a (userId, deviceId) pair. */
function compoundKey(userId: string, deviceId: number): string {
  // \x00 is a control character that cannot appear in a valid userId string,
  // so it is safe as a separator.
  return `${userId}\x00${deviceId}`;
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);

    req.onupgradeneeded = (evt) => {
      const db = (evt.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "_pk" });
        store.createIndex(INDEX_USER, "userId", { unique: false });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * IndexedDB-backed implementation of {@link MultiDeviceStore}.
 *
 * `dbName` is exposed so tests can use fresh isolated databases without
 * requiring `fake-indexeddb`.
 */
export class IdbMultiDeviceStore implements MultiDeviceStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName = "kaether:multi-device:v1") {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  /**
   * Close the cached DB handle. Necessary between tests so each test can
   * open a fresh connection after deleting the previous database.
   */
  close(): void {
    if (this.dbPromise) {
      this.dbPromise.then((db) => db.close()).catch(() => void 0);
      this.dbPromise = null;
    }
  }

  async list(userId: string): Promise<DeviceRecord[]> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const index = tx.objectStore(STORE_NAME).index(INDEX_USER);
      const req = index.getAll(IDBKeyRange.only(userId));
      req.onsuccess = () => {
        const rows: (DeviceRecord & { _pk: string })[] = req.result;
        const records: DeviceRecord[] = rows
          .map(({ _pk: _discarded, ...rec }) => rec as DeviceRecord)
          .sort((a, b) => a.deviceId - b.deviceId);
        resolve(records);
      };
      req.onerror = () => reject(req.error);
    });
  }

  async add(userId: string, record: DeviceRecord): Promise<void> {
    if (record.userId !== userId) {
      throw new Error(
        `MultiDeviceStore.add: record.userId "${record.userId}" does not match userId argument "${userId}"`,
      );
    }
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      // Attach synthetic primary key before storing.
      const row = { ...record, _pk: compoundKey(userId, record.deviceId) };
      const req = store.put(row);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async markRevoked(
    userId: string,
    deviceId: number,
    at: Date = new Date(),
  ): Promise<void> {
    const db = await this.db();
    const pk = compoundKey(userId, deviceId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const getReq = store.get(pk);
      getReq.onsuccess = () => {
        const existing: (DeviceRecord & { _pk: string }) | undefined =
          getReq.result;
        if (!existing) {
          // No-op: device was never registered or was already pruned.
          resolve();
          return;
        }
        const updated: DeviceRecord & { _pk: string } = {
          ...existing,
          status: "revoked",
          lastSeenAt: at.toISOString(),
        };
        const putReq = store.put(updated);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }

  async prune(userId: string, olderThanMs: number): Promise<void> {
    const db = await this.db();
    const cutoff = new Date(Date.now() - olderThanMs).toISOString();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const index = store.index(INDEX_USER);
      const req = index.openCursor(IDBKeyRange.only(userId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          resolve();
          return;
        }
        const rec: DeviceRecord = cursor.value;
        if (rec.lastSeenAt < cutoff) {
          cursor.delete();
        }
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
  }
}

// ---------------------------------------------------------------------------
// Event bus (thin, no Solid dependency)
// ---------------------------------------------------------------------------

export type DeviceRevokedEvent = {
  readonly type: "device-revoked";
  readonly userId: string;
  readonly deviceId: number;
  readonly at: string;
};

export type MultiDeviceEvent = DeviceRevokedEvent;

type Listener = (event: MultiDeviceEvent) => void;

/**
 * Minimal synchronous event emitter for multi-device lifecycle events.
 * Callers (e.g. the session layer) subscribe here to drop Signal sessions
 * when a device is revoked.
 *
 * Usage:
 * ```ts
 * multiDeviceEvents.on((evt) => {
 *   if (evt.type === "device-revoked") {
 *     await signalStore.removeAllSessions(evt.userId); // or per-deviceId
 *   }
 * });
 * ```
 */
export class MultiDeviceEventBus {
  private listeners: Listener[] = [];

  on(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  emit(event: MultiDeviceEvent): void {
    for (const l of this.listeners) {
      try {
        l(event);
      } catch {
        // listeners must not crash the bus
      }
    }
  }
}

/**
 * Module-level singleton event bus. Import and subscribe from the session
 * layer when you need to react to device revocations.
 */
export const multiDeviceEvents = new MultiDeviceEventBus();

/**
 * Convenience wrapper: marks a device revoked in the store AND emits the
 * `device-revoked` event so all subscribers can clean up sessions.
 */
export async function revokeDevice(
  store: IdbMultiDeviceStore,
  userId: string,
  deviceId: number,
  at: Date = new Date(),
): Promise<void> {
  await store.markRevoked(userId, deviceId, at);
  multiDeviceEvents.emit({
    type: "device-revoked",
    userId,
    deviceId,
    at: at.toISOString(),
  });
}
